import DesignTokens from '@tableau/design-tokens';
import { TextAreaWidget, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FormattingHints } from '~src/commonInfra/components/FormattingHints';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { characterCounter } from '~src/commonInfra/utils/CharacterCounter';
import { ATTENTION_COLOR } from '~src/settings/styles/CommonStyles';
import { cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';

export const MAX_DESCRIPTION_LENGTH = 4000;
export const MAX_PROJECT_NAME_LENGTH = 255;
export const PROJECT_NAME_TEST_ID = 'text-field-project-name';
export const PROJECT_DESCRIPTION_TEST_ID = 'text-area-project-description';

export type CreateProjectDialogProps = {
  parentId?: string;
}

export type CreateProjectDialogState = {
  name: string;
  description: string;
  errorMessage: string;
  isProjectDescriptionValid: boolean;
}

export const textField = cssClass('create-project-action', 'textField', {
  width: '100%'
});

export const textArea = cssClass('create-project-action', 'textArea', {
  width: '100%',
  paddingTop: '16px',
});

const textAreaStyles: React.CSSProperties = {
  resize: 'vertical',
  maxHeight: '520px',
  minHeight: '120px'
};

const errorMessageStyle: React.CSSProperties = {
  color: applyColor(DesignTokens.color.component.content.alert.default.error.default, ATTENTION_COLOR)
};

export type CreateProjectActionDialogProps = ReactActionDialogBodyProps<CreateProjectDialogProps, CreateProjectDialogState>;

export function CreateProjectDialogBody(props: CreateProjectActionDialogProps): JSX.Element {
  return <>
    <div><ProjectNameField {...props} /></div>
    <div><ProjectDescriptionField {...props} /></div>
  </>;
}

export function ProjectNameField(props: CreateProjectActionDialogProps): JSX.Element {
  const onTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const projectName = e.target.value;
    props.updateDialogState({ name: projectName });
  };
  return <TextFieldWidget
    kind='outline'
    label={Messages.createProjectDialogPrompt()}
    onChange={onTextFieldChange}
    testId={PROJECT_NAME_TEST_ID}
    style={{ width: '100%' }}
    className={textField}
    value={props.dialogState.name}
    maxLength={MAX_PROJECT_NAME_LENGTH}
  />;
}

export function ProjectDescriptionField(props: CreateProjectActionDialogProps): JSX.Element {
  const textAreaMessage = characterCounter({value: props.dialogState.description, maximum: MAX_DESCRIPTION_LENGTH});
  const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const projectDescription = e.target.value;
    props.updateDialogState({
      isProjectDescriptionValid: projectDescription.length <= MAX_DESCRIPTION_LENGTH,
      description: projectDescription
    });
  };

  return <>
    <TextAreaWidget
      label={Messages.description()}
      testId={PROJECT_DESCRIPTION_TEST_ID}
      message={textAreaMessage}
      rows={16}
      className={textArea}
      style={textAreaStyles}
      onChange={onTextAreaChange}
      value={props.dialogState.description}
      valid={props.dialogState.isProjectDescriptionValid ? undefined : false}
    />
    <FormattingHints showProjectImageHint={true}/>
    {props.dialogState.errorMessage &&
      <div className={'error-message'} style={errorMessageStyle}>{props.dialogState.errorMessage}</div>
    }
  </>;
}
